import React, { useState } from 'react';
import { Row, Col, Card, Accordion, Button } from 'react-bootstrap';

const Help = () => {
  return (
    <div>
      <h2 className="mb-4">Help & Documentation</h2>
      
      <Row>
        <Col md={8}>
          {/* Quick Start */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Quick Start Guide</h5>
            </Card.Header>
            <Card.Body>
              <ol>
                <li className="mb-3">
                  <strong>Upload your data</strong><br />
                  Go to the <a href="/upload">Upload</a> page and select a CSV or JSON file. 
                  You can upload a single file or multiple files at once.
                </li>
                <li className="mb-3">
                  <strong>Wait for processing</strong><br />
                  Odyssey23 will automatically validate your data, detect anomalies, 
                  and create a cleaned version.
                </li>
                <li className="mb-3">
                  <strong>Review results</strong><br />
                  Click on a job in the <a href="/jobs">Jobs</a> list to see detailed results, 
                  including detected anomalies and data preview.
                </li>
                <li className="mb-3">
                  <strong>Provide feedback</strong><br />
                  In the job detail view, mark anomalies as "True Positive" (real issue) or "False Positive" (not an issue) 
                  to help improve the model.
                </li>
                <li className="mb-3">
                  <strong>Download cleaned data</strong><br />
                  Download the cleaned file with issues fixed.
                </li>
              </ol>
            </Card.Body>
          </Card>

          {/* Detailed Instructions */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Detailed Instructions</h5>
            </Card.Header>
            <Card.Body>
              <Accordion>
                <Card>
                  <Accordion.Toggle as={Card.Header} eventKey="0" style={{cursor: 'pointer'}}>
                    <strong>Uploading Files</strong>
                  </Accordion.Toggle>
                  <Accordion.Collapse eventKey="0">
                    <Card.Body>
                      <p><strong>Supported formats:</strong> CSV and JSON files</p>
                      <p><strong>File size limit:</strong> 50MB per file</p>
                      <p><strong>Single upload:</strong> Drag and drop or click to select one file</p>
                      <p><strong>Batch upload:</strong> Select multiple files to process them all at once</p>
                      <p className="text-muted">
                        Tip: For best results, ensure your CSV files have headers in the first row.
                      </p>
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>

                <Card>
                  <Accordion.Toggle as={Card.Header} eventKey="1" style={{cursor: 'pointer'}}>
                    <strong>Understanding Validation Results</strong>
                  </Accordion.Toggle>
                  <Accordion.Collapse eventKey="1">
                    <Card.Body>
                      <p>Odyssey23 checks for these issues:</p>
                      <ul>
                        <li><strong>Missing Values</strong> - Cells with no data (null, NaN, empty)</li>
                        <li><strong>Duplicate Rows</strong> - Identical rows that appear more than once</li>
                        <li><strong>Empty Columns</strong> - Columns where all values are missing</li>
                        <li><strong>Constant Columns</strong> - Columns where all values are the same</li>
                        <li><strong>Type Mismatches</strong> - Columns with inconsistent data types</li>
                      </ul>
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>

                <Card>
                  <Accordion.Toggle as={Card.Header} eventKey="2" style={{cursor: 'pointer'}}>
                    <strong>Understanding Anomaly Detection</strong>
                  </Accordion.Toggle>
                  <Accordion.Collapse eventKey="2">
                    <Card.Body>
                      <p>Anomalies are detected using machine learning:</p>
                      <ul>
                        <li><strong>Isolation Forest</strong> - Identifies outliers by how easily they can be isolated</li>
                        <li><strong>Local Outlier Factor</strong> - Finds points that have a different density than neighbors</li>
                        <li><strong>Z-Score</strong> - Flags values that are far from the column mean</li>
                      </ul>
                      <p className="text-muted">
                        Not all flagged anomalies are errors - some may be legitimate unusual values. 
                        Use the feedback system to tell the model which are real issues.
                      </p>
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>

                <Card>
                  <Accordion.Toggle as={Card.Header} eventKey="3" style={{cursor: 'pointer'}}>
                    <strong>Reviewing and Fixing Anomalies</strong>
                  </Accordion.Toggle>
                  <Accordion.Collapse eventKey="3">
                    <Card.Body>
                      <p>In the job detail view, you can:</p>
                      <ul>
                        <li><strong>View data</strong> - See original and cleaned data with anomalies highlighted in red</li>
                        <li><strong>Mark feedback</strong> - Click ✓ for true positive or ✗ for false positive</li>
                        <li><strong>Fix values</strong> - After marking true positive, you can:
                          <ul>
                            <li>Edit the value (with suggested replacements)</li>
                            <li>Clear the value (set to null)</li>
                            <li>Delete the entire row</li>
                          </ul>
                        </li>
                      </ul>
                      <p><strong>Color coding:</strong></p>
                      <ul>
                        <li><span style={{backgroundColor: '#ffe6e6', padding: '2px 8px'}}>Red</span> - Pending review</li>
                        <li><span style={{backgroundColor: '#fff3cd', padding: '2px 8px'}}>Yellow</span> - True positive (needs fix)</li>
                        <li><span style={{backgroundColor: '#d4edda', padding: '2px 8px'}}>Green</span> - False positive (OK)</li>
                      </ul>
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>

                <Card>
                  <Accordion.Toggle as={Card.Header} eventKey="4" style={{cursor: 'pointer'}}>
                    <strong>Model Training & Feedback</strong>
                  </Accordion.Toggle>
                  <Accordion.Collapse eventKey="4">
                    <Card.Body>
                      <p>
                        Your feedback helps improve the anomaly detection model. When you mark anomalies 
                        as true or false positives:
                      </p>
                      <ul>
                        <li>The model learns from your corrections</li>
                        <li>Contamination rate adjusts based on precision</li>
                        <li>Future uploads benefit from improved detection</li>
                      </ul>
                      <p>
                        Go to <a href="/models">Models</a> to see model versions and manually trigger retraining.
                      </p>
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>

                <Card>
                  <Accordion.Toggle as={Card.Header} eventKey="5" style={{cursor: 'pointer'}}>
                    <strong>Downloading Results</strong>
                  </Accordion.Toggle>
                  <Accordion.Collapse eventKey="5">
                    <Card.Body>
                      <p>You can download the cleaned file from:</p>
                      <ul>
                        <li>The job detail page (green "Download Cleaned File" button)</li>
                        <li>The jobs list (download icon)</li>
                      </ul>
                      <p>
                        The cleaned file includes all fixes from the automatic cleaning process. 
                        If you made manual edits to anomalies, those are included too.
                      </p>
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>
              </Accordion>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          {/* FAQ */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">FAQ</h5>
            </Card.Header>
            <Card.Body>
              <p><strong>Q: What happens to my data?</strong></p>
              <p className="text-muted mb-3">
                Your uploaded files are processed on our servers and stored temporarily. 
                Cleaned files are available for download. You can delete jobs when done.
              </p>

              <p><strong>Q: Why was this flagged as an anomaly?</strong></p>
              <p className="text-muted mb-3">
                The ML model considers a value anomalous if it's statistically unusual 
                compared to other values in the dataset. Hover over highlighted cells 
                to see the anomaly score.
              </p>

              <p><strong>Q: Can I undo changes?</strong></p>
              <p className="text-muted mb-3">
                The original file is always preserved. The cleaned file is a separate copy. 
                You can re-upload to start fresh.
              </p>

              <p><strong>Q: What file formats are supported?</strong></p>
              <p className="text-muted">
                CSV and JSON files up to 50MB.
              </p>
            </Card.Body>
          </Card>

          {/* Tips */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Tips</h5>
            </Card.Header>
            <Card.Body>
              <ul>
                <li>Use batch upload for processing multiple related files</li>
                <li>Review anomalies from highest to lowest score</li>
                <li>Provide feedback on at least 10-20 anomalies before retraining</li>
                <li>Check the Dashboard for an overview of recent activity</li>
              </ul>
            </Card.Body>
          </Card>

          {/* Contact */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">Need More Help?</h5>
            </Card.Header>
            <Card.Body>
              <p>Can't find what you're looking for?</p>
              <p>
                <a href="mailto:info@odyssey23.com">info@odyssey23.com</a>
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Help;